// Image size & quality check for every image upload in the back office.
//
// One picture is used for both the website and the mobile app, so each kind
// of image has one recommended size (sharp on a large desktop screen, light
// enough for mobile data) and a minimum below which it looks blurry.
//
//   import { IMAGE_SPECS, imageSpecText, checkImage } from '../../utils/imageCheck';
//   const use = await checkImage(file, IMAGE_SPECS.product);  // popup when something is off
//   if (!use) return;                                         // "Choose another" / blocked
//   ...then upload `use` (the original, or the resized picture)
//
// Checks: not an image, too small (blocked), wrong shape, file too big,
// heavily compressed, blurry / enlarged from a small picture. Every problem
// is shown in one popup with the reason and what to do; small problems can
// be accepted with "Use anyway".
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Typography,
} from '@mui/material';

export type ImageSpec = {
  label: string;
  w: number; // recommended size
  h: number;
  minW: number; // smaller than this = blocked
  minH: number;
  maxKB: number; // bigger file = warning (suggest compressing)
  note?: string;
};

export const IMAGE_SPECS: Record<
  'bannerTextImage' | 'bannerFull' | 'subBanner' | 'offerBanner' | 'offerImage' | 'product' | 'category' | 'bundle' | 'colorSwatch',
  ImageSpec
> = {
  bannerTextImage: {
    label: 'Main banner (Text + Image layout)',
    w: 1280, h: 1000, minW: 960, minH: 750, maxKB: 400,
    note: 'Keep the product in the middle; text is placed next to it.',
  },
  bannerFull: {
    label: 'Main banner (Full Image layout)',
    w: 1920, h: 528, minW: 1600, minH: 440, maxKB: 450,
    note: 'Keep text and faces inside the middle 1200 px - wide screens may trim the sides.',
  },
  subBanner: {
    label: 'Sub banner',
    w: 800, h: 800, minW: 600, minH: 600, maxKB: 250,
  },
  offerBanner: {
    label: 'Offer banner',
    w: 1600, h: 400, minW: 1200, minH: 300, maxKB: 250,
    note: 'Same picture on web and app (web shows two side by side, app one at a time). Keep text 60 px away from the edges.',
  },
  offerImage: {
    label: 'Offer image',
    w: 800, h: 800, minW: 600, minH: 600, maxKB: 250,
  },
  product: {
    label: 'Product image',
    w: 1200, h: 1200, minW: 800, minH: 800, maxKB: 300,
    note: 'White or light background, product filling about 85% of the square.',
  },
  category: {
    label: 'Category image',
    w: 600, h: 600, minW: 400, minH: 400, maxKB: 150,
  },
  bundle: {
    label: 'Bundle image',
    w: 1200, h: 1200, minW: 800, minH: 800, maxKB: 300,
  },
  colorSwatch: {
    label: 'Colour image',
    w: 200, h: 200, minW: 80, minH: 80, maxKB: 80,
  },
};

const ratioText = (w: number, h: number) => {
  const r = w / h;
  if (Math.abs(r - 1) < 0.02) return 'square';
  return r > 1 ? `${r.toFixed(2)} : 1` : `1 : ${(1 / r).toFixed(2)}`;
};

// one line for under the upload box
export const imageSpecText = (s: ImageSpec) =>
  `Recommended ${s.w} × ${s.h} px (${ratioText(s.w, s.h)}), at least ${s.minW} × ${s.minH} px. ` +
  `JPG or WebP at 80–85% quality, under ${s.maxKB} KB.${s.note ? ' ' + s.note : ''}`;

type Issue = { level: 'block' | 'warn'; title: string; text: string };

const loadImage = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('not an image'));
    };
    img.src = url;
  });

// Sharpness: edge strength of the picture shrunk to 512 px. A blurry photo,
// or a small photo enlarged, has weak edges everywhere. Large plain areas
// (white background) are ignored - only the strongest edges are measured.
const sharpness = (img: HTMLImageElement) => {
  const scale = Math.min(1, 512 / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(8, Math.round(img.naturalWidth * scale));
  const h = Math.max(8, Math.round(img.naturalHeight * scale));
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);
  const g = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) g[i] = 0.299 * data[i * 4] + 0.587 * data[i * 4 + 1] + 0.114 * data[i * 4 + 2];
  const vals: number[] = [];
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = y * w + x;
      const lap = Math.abs(4 * g[i] - g[i - 1] - g[i + 1] - g[i - w] - g[i + w]);
      if (lap > 2) vals.push(lap); // skip flat areas
    }
  }
  if (vals.length < 200) return null; // almost plain picture: can't judge
  vals.sort((a, b) => a - b);
  return vals[Math.floor(vals.length * 0.95)]; // strongest 5% of edges
};

const analyse = async (file: File, spec: ImageSpec): Promise<{ issues: Issue[]; info: string }> => {
  const issues: Issue[] = [];
  if (!file.type.startsWith('image/')) {
    return { issues: [{ level: 'block', title: 'Not an image', text: 'Please choose a JPG, PNG or WebP picture.' }], info: '' };
  }
  let img: HTMLImageElement;
  try {
    img = await loadImage(file);
  } catch {
    return { issues: [{ level: 'block', title: 'Picture cannot be read', text: 'The file looks damaged. Please export it again and retry.' }], info: '' };
  }
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const kb = Math.round(file.size / 1024);
  const info = `Your picture: ${w} × ${h} px (${ratioText(w, h)}), ${kb >= 1024 ? (kb / 1024).toFixed(1) + ' MB' : kb + ' KB'}`;

  // 1. size
  if (w < spec.minW || h < spec.minH) {
    issues.push({
      level: 'block',
      title: 'Picture too small',
      text: `It needs at least ${spec.minW} × ${spec.minH} px, otherwise it looks blurry on big screens. Recommended: ${spec.w} × ${spec.h} px.`,
    });
  } else if (w < spec.w || h < spec.h) {
    issues.push({
      level: 'warn',
      title: 'Smaller than recommended',
      text: `It's usable, but ${spec.w} × ${spec.h} px stays sharper on large desktop screens.`,
    });
  }

  // 2. shape
  const want = spec.w / spec.h;
  const got = w / h;
  if (Math.abs(got / want - 1) > 0.05) {
    issues.push({
      level: 'warn',
      title: 'Different shape',
      text: `This is ${ratioText(w, h)}, the recommended shape is ${ratioText(spec.w, spec.h)} (${spec.w} × ${spec.h} px). Parts of the picture may be cut off or empty bands shown. Crop it to ${spec.w} × ${spec.h} px.`,
    });
  }

  // 3. file size / compression
  if (kb > spec.maxKB) {
    issues.push({
      level: 'warn',
      title: 'File is heavy',
      text: `${kb} KB - pages load slower on mobile data. Save it as WebP or JPG at 80–85% quality to get it under ${spec.maxKB} KB (no visible difference).`,
    });
  }
  const bytesPerPixel = file.size / (w * h);
  if (/jpe?g|webp/i.test(file.type) && w * h >= 400 * 400 && bytesPerPixel < 0.04) {
    issues.push({
      level: 'warn',
      title: 'Strongly compressed',
      text: 'The picture was saved at very low quality - blocks and smudges may show. Export it again at 80–85% quality.',
    });
  }

  // 4. sharpness (blurry or enlarged from a small picture)
  try {
    const sharp = sharpness(img);
    if (sharp !== null && sharp < 14) {
      issues.push({
        level: 'warn',
        title: 'Looks blurry',
        text: 'The picture has very soft edges - it may be out of focus or enlarged from a smaller picture. Use the original, larger photo if you have it.',
      });
    }
  } catch {
    // canvas not available: skip this check
  }
  URL.revokeObjectURL(img.src);
  return { issues, info };
};

// ── Resize & crop editor ────────────────────────────────────────────
// Three ways, saved at the recommended size (JPG 85%; PNG for colour
// pictures, keeping see-through parts):
//  - Crop (like on a phone): the whole picture with a crop box on it -
//    drag the corners / edges to resize the box, drag inside it to move it.
//    "Keep shape" locks the box to the recommended shape; unlocked, the
//    cropped part is placed in the frame with a plain background around it.
//  - Fill: the picture fills the frame - drag to place, slider to zoom.
//  - Fit whole picture: the whole picture with a plain background around it.
type Rect = { x: number; y: number; w: number; h: number }; // in picture pixels

function ResizeEditor({ src, spec, fileName, keepPng, onCancel, onSave }: {
  src: string; spec: ImageSpec; fileName: string; keepPng: boolean;
  onCancel: () => void; onSave: (f: File) => void;
}) {
  const [img, setImg] = React.useState<HTMLImageElement | null>(null);
  const [mode, setMode] = React.useState<'crop' | 'fill' | 'fit'>('crop');
  const [zoom, setZoom] = React.useState(1); // 1 = just covers (fill) / just fits (fit)
  const [pos, setPos] = React.useState({ x: 0, y: 0 }); // offset in frame px
  const [bg, setBg] = React.useState('#FFFFFF');
  const [lock, setLock] = React.useState(true); // crop box keeps the recommended shape
  const [crop, setCrop] = React.useState<Rect | null>(null);
  const [saving, setSaving] = React.useState(false);
  const drag = React.useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const cropDrag = React.useRef<{ kind: string; sx: number; sy: number; start: Rect } | null>(null);

  const ratio = spec.w / spec.h;
  const maxW = Math.min(620, typeof window !== 'undefined' ? window.innerWidth - 96 : 620);
  // fill / fit: frame of the recommended shape
  const viewH = Math.min(Math.round(maxW / ratio), 420);
  const viewW = Math.round(viewH * ratio);

  React.useEffect(() => {
    const i = new Image();
    i.onload = () => setImg(i);
    i.src = src;
  }, [src]);

  // biggest box of the recommended shape, centred
  const defaultCrop = React.useCallback((i: HTMLImageElement, keep: boolean): Rect => {
    const W = i.naturalWidth;
    const H = i.naturalHeight;
    if (!keep) return { x: W * 0.05, y: H * 0.05, w: W * 0.9, h: H * 0.9 };
    let w = W;
    let h = w / ratio;
    if (h > H) {
      h = H;
      w = h * ratio;
    }
    return { x: (W - w) / 2, y: (H - h) / 2, w, h };
  }, [ratio]);
  React.useEffect(() => {
    if (img) setCrop(defaultCrop(img, lock));
  }, [img, lock, defaultCrop]);

  // ── fill / fit geometry ──
  const base = img
    ? mode === 'fit'
      ? Math.min(viewW / img.naturalWidth, viewH / img.naturalHeight)
      : Math.max(viewW / img.naturalWidth, viewH / img.naturalHeight)
    : 1;
  const scale = base * zoom;
  const dw = img ? img.naturalWidth * scale : 0;
  const dh = img ? img.naturalHeight * scale : 0;
  const clamp = (p: { x: number; y: number }) => {
    if (mode !== 'fill') return p;
    const mx = Math.max(0, (dw - viewW) / 2);
    const my = Math.max(0, (dh - viewH) / 2);
    return { x: Math.max(-mx, Math.min(mx, p.x)), y: Math.max(-my, Math.min(my, p.y)) };
  };
  const shown = clamp(pos);
  const left = (viewW - dw) / 2 + shown.x;
  const top = (viewH - dh) / 2 + shown.y;

  // ── crop geometry: whole picture shown in a box ──
  const cs = img ? Math.min(maxW / img.naturalWidth, 420 / img.naturalHeight) : 1; // picture px -> screen px
  const cw = img ? Math.round(img.naturalWidth * cs) : 0;
  const ch = img ? Math.round(img.naturalHeight * cs) : 0;

  const fixCrop = (r: Rect): Rect => {
    if (!img) return r;
    const W = img.naturalWidth;
    const H = img.naturalHeight;
    const min = Math.max(24 / cs, 20);
    let { x, y, w, h } = r;
    w = Math.max(min, Math.min(w, W));
    h = Math.max(min, Math.min(h, H));
    if (lock) {
      // keep the shape: follow the side that changed most
      if (w / h > ratio) w = h * ratio;
      else h = w / ratio;
      if (w > W) { w = W; h = w / ratio; }
      if (h > H) { h = H; w = h * ratio; }
    }
    x = Math.max(0, Math.min(x, W - w));
    y = Math.max(0, Math.min(y, H - h));
    return { x, y, w, h };
  };

  const cropDown = (kind: string) => (e: React.PointerEvent) => {
    if (!crop) return;
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    cropDrag.current = { kind, sx: e.clientX, sy: e.clientY, start: { ...crop } };
  };
  const cropMove = (e: React.PointerEvent) => {
    const d = cropDrag.current;
    if (!d) return;
    const dx = (e.clientX - d.sx) / cs;
    const dy = (e.clientY - d.sy) / cs;
    const s0 = d.start;
    let r: Rect = { ...s0 };
    if (d.kind === 'move') {
      r = { ...s0, x: s0.x + dx, y: s0.y + dy };
      setCrop(fixCrop(r));
      return;
    }
    // corners / edges: the opposite side stays where it is
    const right = s0.x + s0.w;
    const bottom = s0.y + s0.h;
    if (d.kind.includes('l')) { r.x = s0.x + dx; r.w = s0.w - dx; }
    if (d.kind.includes('r')) { r.w = s0.w + dx; }
    if (d.kind.includes('t')) { r.y = s0.y + dy; r.h = s0.h - dy; }
    if (d.kind.includes('b')) { r.h = s0.h + dy; }
    if (lock) {
      // shape locked: size from the width (or height for top / bottom edges)
      if (d.kind === 't' || d.kind === 'b') r.w = r.h * ratio;
      else r.h = r.w / ratio;
      if (d.kind.includes('l')) r.x = right - r.w;
      if (d.kind.includes('t')) r.y = bottom - r.h;
      if (d.kind === 't' || d.kind === 'b') r.x = s0.x + (s0.w - r.w) / 2;
      if (d.kind === 'l' || d.kind === 'r') r.y = s0.y + (s0.h - r.h) / 2;
    }
    const fixed = fixCrop(r);
    // keep the fixed side fixed after limits
    if (d.kind.includes('l')) fixed.x = Math.max(0, right - fixed.w);
    if (d.kind.includes('t')) fixed.y = Math.max(0, bottom - fixed.h);
    setCrop(fixed);
  };
  const cropUp = () => {
    cropDrag.current = null;
  };

  // part of the original that ends up in the picture (for the "enlarged" note)
  const usedPx = !img
    ? 0
    : mode === 'crop'
    ? crop?.w || 0
    : mode === 'fill'
    ? Math.min(img.naturalWidth, viewW / scale)
    : spec.w;
  const enlarged = !!img && mode !== 'fit' && usedPx < spec.w * 0.98;

  const save = () => {
    if (!img) return;
    const c = document.createElement('canvas');
    c.width = spec.w;
    c.height = spec.h;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    setSaving(true);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const plain = mode === 'fit' || (mode === 'crop' && !lock);
    if (!keepPng || plain) {
      if (!(keepPng && plain)) {
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, spec.w, spec.h);
      }
    }
    if (mode === 'crop' && crop) {
      if (lock) {
        ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, spec.w, spec.h);
      } else {
        const k = Math.min(spec.w / crop.w, spec.h / crop.h);
        const ow = crop.w * k;
        const oh = crop.h * k;
        ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, (spec.w - ow) / 2, (spec.h - oh) / 2, ow, oh);
      }
    } else {
      const k = spec.w / viewW; // frame px -> output px
      ctx.drawImage(img, left * k, top * k, dw * k, dh * k);
    }
    const type = keepPng ? 'image/png' : 'image/jpeg';
    c.toBlob(
      blob => {
        setSaving(false);
        if (!blob) return;
        const name = fileName.replace(/\.[^.]+$/, '') + `_${spec.w}x${spec.h}` + (keepPng ? '.png' : '.jpg');
        onSave(new File([blob], name, { type }));
      },
      type,
      0.85,
    );
  };

  const down = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, px: shown.x, py: shown.y };
  };
  const move = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setPos(clamp({ x: drag.current.px + e.clientX - drag.current.x, y: drag.current.py + e.clientY - drag.current.y }));
  };
  const up = () => {
    drag.current = null;
  };

  const showBg = mode === 'fit' || (mode === 'crop' && !lock);
  const handle = (kind: string, sx: object) => (
    <Box
      key={kind}
      onPointerDown={cropDown(kind)}
      sx={{ position: 'absolute', width: 18, height: 18, bgcolor: '#fff', border: '2px solid #F78E12', borderRadius: '4px', zIndex: 3, ...sx }}
    />
  );
  const edge = (kind: string, sx: object) => (
    <Box key={kind} onPointerDown={cropDown(kind)} sx={{ position: 'absolute', zIndex: 2, ...sx }} />
  );

  return (
    <Dialog open onClose={onCancel} maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>Resize to {spec.w} × {spec.h} px</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: '#6d7175', mb: 1.5 }}>
          {mode === 'crop'
            ? `${spec.label}: drag the corners or edges of the box to choose the part to keep, drag inside it to move it.`
            : `${spec.label}: drag the picture to place it, use the slider to zoom.`}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }} alignItems="center">
          {(['crop', 'fill', 'fit'] as const).map(m => (
            <Button
              key={m}
              size="small"
              variant="contained"
              disableElevation
              color={mode === m ? 'primary' : 'inherit'}
              onClick={() => {
                setMode(m);
                setZoom(1);
                setPos({ x: 0, y: 0 });
              }}
            >
              {m === 'crop' ? 'Crop' : m === 'fill' ? 'Fill frame' : 'Fit whole picture'}
            </Button>
          ))}
          {mode === 'crop' && (
            <Button
              size="small"
              variant="contained"
              disableElevation
              color={lock ? 'warning' : 'inherit'}
              onClick={() => setLock(v => !v)}
            >
              {lock ? `🔒 Keep shape ${spec.w}:${spec.h}` : '🔓 Free crop'}
            </Button>
          )}
          {showBg && !keepPng && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pl: 1 }}>
              <Typography variant="caption">Background</Typography>
              {['#FFFFFF', '#F5F5F5', '#000000'].map(c => (
                <Box
                  key={c}
                  onClick={() => setBg(c)}
                  sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: c, cursor: 'pointer', border: bg === c ? '2px solid #F78E12' : '1px solid #c9cccf' }}
                />
              ))}
            </Stack>
          )}
        </Stack>

        {mode === 'crop' ? (
          // whole picture + crop box
          <Box
            onPointerMove={cropMove}
            onPointerUp={cropUp}
            onPointerCancel={cropUp}
            sx={{ position: 'relative', width: cw, height: ch, mx: 'auto', userSelect: 'none', touchAction: 'none', bgcolor: '#f0f0f0' }}
          >
            {img && <img src={src} alt="" draggable={false} style={{ width: cw, height: ch, display: 'block', pointerEvents: 'none' }} />}
            {img && crop && (() => {
              const x = crop.x * cs;
              const y = crop.y * cs;
              const w = crop.w * cs;
              const h = crop.h * cs;
              const dim = { position: 'absolute', bgcolor: 'rgba(0,0,0,0.55)', pointerEvents: 'none' } as const;
              return (
                <>
                  <Box sx={{ ...dim, left: 0, top: 0, width: cw, height: y }} />
                  <Box sx={{ ...dim, left: 0, top: y + h, width: cw, height: ch - y - h }} />
                  <Box sx={{ ...dim, left: 0, top: y, width: x, height: h }} />
                  <Box sx={{ ...dim, left: x + w, top: y, width: cw - x - w, height: h }} />
                  {/* the box: drag inside to move; thirds grid like a phone */}
                  <Box
                    onPointerDown={cropDown('move')}
                    sx={{
                      position: 'absolute', left: x, top: y, width: w, height: h, cursor: 'move', zIndex: 1,
                      outline: '2px solid #F78E12',
                      backgroundImage:
                        'linear-gradient(to right, transparent 33.1%, rgba(255,255,255,0.6) 33.3%, transparent 33.5%, transparent 66.5%, rgba(255,255,255,0.6) 66.7%, transparent 66.9%),' +
                        'linear-gradient(to bottom, transparent 33.1%, rgba(255,255,255,0.6) 33.3%, transparent 33.5%, transparent 66.5%, rgba(255,255,255,0.6) 66.7%, transparent 66.9%)',
                    }}
                  />
                  {edge('t', { left: x + 12, top: y - 6, width: Math.max(0, w - 24), height: 12, cursor: 'ns-resize' })}
                  {edge('b', { left: x + 12, top: y + h - 6, width: Math.max(0, w - 24), height: 12, cursor: 'ns-resize' })}
                  {edge('l', { left: x - 6, top: y + 12, width: 12, height: Math.max(0, h - 24), cursor: 'ew-resize' })}
                  {edge('r', { left: x + w - 6, top: y + 12, width: 12, height: Math.max(0, h - 24), cursor: 'ew-resize' })}
                  {handle('tl', { left: x - 9, top: y - 9, cursor: 'nwse-resize' })}
                  {handle('tr', { left: x + w - 9, top: y - 9, cursor: 'nesw-resize' })}
                  {handle('bl', { left: x - 9, top: y + h - 9, cursor: 'nesw-resize' })}
                  {handle('br', { left: x + w - 9, top: y + h - 9, cursor: 'nwse-resize' })}
                </>
              );
            })()}
          </Box>
        ) : (
          <Box
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            sx={{
              position: 'relative',
              width: viewW,
              height: viewH,
              mx: 'auto',
              overflow: 'hidden',
              borderRadius: '8px',
              bgcolor: mode === 'fit' ? bg : '#f0f0f0',
              outline: '2px solid #F78E12',
              cursor: 'grab',
              touchAction: 'none',
              userSelect: 'none',
            }}
          >
            {img && (
              <img
                src={src}
                alt=""
                draggable={false}
                style={{ position: 'absolute', left, top, width: dw, height: dh, maxWidth: 'none', pointerEvents: 'none' }}
              />
            )}
          </Box>
        )}

        {mode !== 'crop' ? (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, px: 1 }}>
            <Typography variant="caption">Zoom</Typography>
            <Slider size="small" min={mode === 'fit' ? 0.5 : 1} max={4} step={0.01} value={zoom} onChange={(_, v) => setZoom(v as number)} />
          </Stack>
        ) : (
          crop && (
            <Typography variant="caption" sx={{ display: 'block', color: '#6d7175', mt: 1, textAlign: 'center' }}>
              Selected {Math.round(crop.w)} × {Math.round(crop.h)} px of the original → saved as {spec.w} × {spec.h} px
            </Typography>
          )
        )}
        {enlarged && (
          <Typography variant="caption" sx={{ display: 'block', color: '#9a5b00', mt: 1 }}>
            ⚠ The part you keep is smaller than {spec.w} px across in the original - it will be enlarged and may look soft.
            {mode === 'crop' ? ' Make the box bigger if you can.' : ' Zoom out a little if you can.'}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" color="inherit" disableElevation onClick={onCancel}>
          Back
        </Button>
        <Button variant="contained" disableElevation onClick={save} disabled={!img || saving}>
          {saving ? 'Saving…' : 'Use resized picture'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CheckDialog({ spec, info, issues, canResize, onDone, onResize }: {
  spec: ImageSpec; info: string; issues: Issue[]; canResize: boolean;
  onDone: (ok: boolean) => void; onResize: () => void;
}) {
  const blocked = issues.some(i => i.level === 'block');
  return (
    <Dialog open onClose={() => onDone(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{blocked ? 'Please use another picture' : 'Check this picture'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: '#6d7175', mb: 2 }}>
          {spec.label} · {info}
        </Typography>
        <Stack spacing={1.5}>
          {issues.map((i, k) => (
            <Box
              key={k}
              sx={{
                p: 1.5,
                borderRadius: '10px',
                bgcolor: i.level === 'block' ? '#fdecea' : '#fff8ed',
                border: `1px solid ${i.level === 'block' ? '#f5c2bd' : '#ffe1b3'}`,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: i.level === 'block' ? '#b42318' : '#9a5b00' }}>
                {i.level === 'block' ? '✕ ' : '⚠ '}
                {i.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#202223' }}>
                {i.text}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Box sx={{ mt: 2, p: 1.5, borderRadius: '10px', bgcolor: '#f6f6f7' }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
            📐 Recommended for {spec.label}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#6d7175' }}>
            {imageSpecText(spec)}
          </Typography>
        </Box>
        {canResize && (
          <Typography variant="caption" sx={{ display: 'block', color: '#6d7175', mt: 1.5 }}>
            Tip: "Resize to fit" crops / scales the picture to {spec.w} × {spec.h} px and saves it smaller - right here, no other tool needed.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        {!blocked && (
          <Button variant="contained" color="inherit" disableElevation onClick={() => onDone(true)}>
            Use anyway
          </Button>
        )}
        <Button variant="contained" color="inherit" disableElevation onClick={() => onDone(false)}>
          Choose another
        </Button>
        {canResize && (
          <Button variant="contained" disableElevation onClick={onResize}>
            Resize to fit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// opens a dialog outside the page's own React tree and waits for its answer
const showDialog = <T,>(render: (done: (v: T) => void) => React.ReactElement) =>
  new Promise<T>(resolve => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    root.render(
      render(v => {
        root.unmount();
        host.remove();
        resolve(v);
      }),
    );
  });

/** Resize / crop a picture to the recommended size. null = cancelled. */
export async function resizeImage(file: File, spec: ImageSpec): Promise<File | null> {
  const src = URL.createObjectURL(file);
  const keepPng = file.type === 'image/png' && spec === IMAGE_SPECS.colorSwatch;
  const out = await showDialog<File | null>(done => (
    <ResizeEditor src={src} spec={spec} fileName={file.name} keepPng={keepPng} onCancel={() => done(null)} onSave={f => done(f)} />
  ));
  URL.revokeObjectURL(src);
  return out;
}

/**
 * Checks the picture; when something is off a popup explains it and offers
 * Use anyway / Choose another / Resize to fit.
 * Returns the file to use (the original or the resized one), or null.
 */
export async function checkImage(file: File | null | undefined, spec: ImageSpec): Promise<File | null> {
  if (!file) return null;
  const { issues, info } = await analyse(file, spec);
  if (!issues.length) return file;
  const blocked = issues.some(i => i.level === 'block');
  // too small / unreadable can't be fixed by resizing (enlarging = blurry)
  const canResize = !blocked && file.type.startsWith('image/');
  for (;;) {
    const answer = await showDialog<'use' | 'cancel' | 'resize'>(done => (
      <CheckDialog
        spec={spec}
        info={info}
        issues={issues}
        canResize={canResize}
        onDone={ok => done(ok ? 'use' : 'cancel')}
        onResize={() => done('resize')}
      />
    ));
    if (answer === 'use') return file;
    if (answer === 'cancel') return null;
    const resized = await resizeImage(file, spec);
    if (resized) return resized; // "Back" in the editor -> the check popup again
  }
}
